import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UISettingsProvider } from './contexts/UISettingsContext';
import { routeConfigs, getRedirectRoutes, getTodayPath } from './config/routes.config';
import RouteContent from './components/routing/RouteRenderer';
import TrackedRoute from './components/routing/TrackedRoute';
import QuickCommand from './components/QuickCommand';
import FloatingChat from './components/FloatingChat';
import './App.css';

// Declare Eruda types for TypeScript
declare global {
  interface Window {
    eruda?: {
      init: () => void;
      destroy: () => void;
    };
  }
}

// Hook to manage Eruda initialization based on user preference
function useEruda(showDebugIcon: boolean, isMobile: boolean) {
  useEffect(() => {
    // Only proceed if on mobile device
    if (!isMobile) {
      return;
    }

    let script: HTMLScriptElement | null = null;

    if (showDebugIcon) {
      // Load Eruda script if not already loaded
      if (!window.eruda) {
        script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/eruda';
        script.async = true;
        script.onload = () => {
          if (window.eruda) {
            window.eruda.init();
          }
        };
        document.head.appendChild(script);
      } else {
        // Eruda already loaded, check if it needs to be initialized
        // Try to init (it will handle if already initialized)
        try {
          window.eruda.init();
        } catch (e) {
          // Eruda might already be initialized, that's okay
          console.log('Eruda already initialized');
        }
      }
    } else {
      // Destroy Eruda if it exists and setting is off
      if (window.eruda) {
        try {
          window.eruda.destroy();
        } catch (e) {
          // Eruda might not be initialized, that's okay
          console.log('Eruda not initialized or already destroyed');
        }
      }
    }

    // Cleanup function
    return () => {
      // Only cleanup if setting is turned off
      if (!showDebugIcon && window.eruda) {
        try {
          window.eruda.destroy();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    };
  }, [showDebugIcon, isMobile]);
}

// Create a wrapper component to use the auth context
function AppContent() {
  const { user, loading: authLoading } = useAuth();

  
  // Check if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Use Eruda hook based on user preference
  useEruda(user?.show_debug_icon ?? false, isMobile);

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <h2>📚 Loading LifeOS..</h2>
          <div className="loading-spinner"></div>
          <p>Initializing your LifeOS...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* All protected routes from config */}
        {routeConfigs.map((config) => (
          <Route 
            key={config.path} 
            path={config.path} 
            element={
              <TrackedRoute user={user}>
                <RouteContent config={config} />
              </TrackedRoute>
            } 
          />
        ))}

        {/* Redirect routes */}
        {getRedirectRoutes().map(({ from, to }) => (
          <Route 
            key={from} 
            path={from} 
            element={<Navigate to={to} replace />} 
          />
        ))}

        {/* Catch-all fallback to today's date */}
        <Route 
          path="/*" 
          element={<Navigate to={getTodayPath()} replace />} 
        />
      </Routes>
      {(user?.show_quick_command ?? true) && <QuickCommand />}
      {(user?.show_floating_chat ?? true) && <FloatingChat />}
    </>
  );
}

// Main App component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <UISettingsProvider>
        <AppContent />
      </UISettingsProvider>
    </AuthProvider>
  );
}

export default App;